import {Injectable} from "@angular/core";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {Observable} from "rxjs/Observable";
import gql from "graphql-tag";
import {IDepot} from "../../shared/redux-store/depot/depot.types";
import {NgRedux, select} from "@angular-redux/store";
import {StockInput} from "./stock-input.type";
import {Stock} from "../../shared/view-model/stock";
import {ApolloQueryResult} from "apollo-client";
import {VisbosService} from "../../core/visbos-core/graphql/visbos.service";
import {productBatchFragments} from "./product-services/product.service";
import {MessageType} from "../../shared/redux-store/session/session.types";
import {medicineFragments} from "./medicine.service";
import {IAppState} from "../../shared/redux-store";
import {SessionActions} from "../../shared/actions";
import {keywordFragments} from "../../core/visbos-core/services/keyword.service";
import {warehousingFragments} from "./warehousing-service/warehousing.service";
import {storeManagementFragments} from "./withdrawal.service";
import {
  FetchExpiringStocksQuery,
  FetchRelatedStocksQuery,
  FetchStocksInDepotsQuery,
  FindProductBatchesQuery,
  MedicineStockChangeInput,
  MoveStocksInput,
  MoveStocksMutation,
  RearrangementInput,
  RemoveProductBatchFromStockMutation
} from "../../shared/graphql/graphql.types";
import {catchError, map} from "rxjs/operators";
import {forkJoin} from "rxjs/observable/forkJoin";
import {empty} from "rxjs/observable/empty";
import {of} from "rxjs/observable/of";
import {environment} from '../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';

export const exportStockListEndpoint = environment.exportUrl + 'stock-list/:adminUnitCuid';

export const stockFragments = {
  stock: gql`
    fragment StockDetailFields on Stock {
      cuid, label, name,
      stock_item {
        cuid, label
        category {cuid label}
        administration_unit {cuid label}
        visbos_article_number
        default_product {
          cuid label
          supplier {cuid label}
        }
        keywords { ...KeywordFields }
      },
      depot {
        cuid, label administration_unit {cuid label}
      },
      depot_yard { cuid, label, name },
      min_stock, report_stock, shall_stock, act_stock,
      order_amount, extraction_amount,
      registration_required,
      identities { cuid, label, ean, ean_type },
      product_batches { ...ProductBatchFields },
      keywords { ...KeywordFields },
      in_shopping_cart, has_open_orders
    }

    ${productBatchFragments.productBatch}
    ${keywordFragments.keyword}
  `
};

export const fetchStocksForDepotQuery = gql`
  query FetchStocksForDepotQuery($adminUnitCuid: String!, $depotId: String) {
    fetchStocks(administration_unit_cuid: $adminUnitCuid, depotId: $depotId) {
      ...StockDetailFields
    }
  }
  ${stockFragments.stock}
`;

export const fetchStocksForSubAdminUnits = gql`
  query StocksForSubAdminUnits($adminUnitCuid: String!, $onlyCritical: Boolean = true) {
    stocksForSubAdminUnits(parent_administration_unit_cuid: $adminUnitCuid, only_critical: $onlyCritical) {
      ...StockDetailFields
      depot {
        administration_unit { cuid, label }
      }
    }
  }
  ${stockFragments.stock}
`;

export const fetchRelatedStocks = gql`
  query FetchRelatedStocks($cuid: String!) {
    relatedStocks(cuid: $cuid) {
      ...StockDetailFields
    }
  }
  ${stockFragments.stock}
`;

export const fetchStocksInDepots = gql`
  query FetchStocksInDepots($src:String!, $target:String!, $critical: Boolean) {
    stocksInDepots(source_depot_cuid: $src, target_depot_cuid: $target,
      only_critical: $critical) {
      cuid label
      depot {cuid label}
      depot_yard {cuid label}
      identities {cuid label ean}
      min_stock report_stock act_stock shall_stock
      related_stocks(depotCuid:$target) {
        cuid label
        min_stock report_stock act_stock shall_stock
        depot {cuid label}
        depot_yard {cuid label}
        identities {cuid label ean}
      }
    }
  }
`;

export const moveStocks = gql`
  mutation MoveStocks($data:[MoveStocksInput], $auth_data: MedicineStockChangeInput, $generateVouchers: Boolean, $rearrangementData: RearrangementInput) {
    moveStocks(data: $data, auth_data: $auth_data, generate_vouchers: $generateVouchers, rearrangement_data: $rearrangementData) {
      withdrawal {
        ...WithdrawalFields
      }
      warehousing {
        ...WarehousingFields
      }
      voucher_withdrawal,
      voucher_warehousing
    }
  }
  ${warehousingFragments.warehousing}
  ${storeManagementFragments.withdrawal}
`;

export const fetchExpiringStocks = gql`
  query FetchExpiringStocks($adminUnitCuid: String, $depotCuid: String,
    $categoryCuid:String, $supplierCuid: String, $expiringRangeDays: Int) {
    expiringStocks(adminUnitCuid: $adminUnitCuid, depotCuid: $depotCuid,
      categoryCuid: $categoryCuid, supplierCuid: $supplierCuid, expiringRangeDays: $expiringRangeDays) {
      cuid label
      min_stock report_stock shall_stock act_stock
      stock_item {cuid}
      depot {
        cuid label
        administration_unit {cuid label}
      }
      depot_yard {cuid label}
      product_batches {
        cuid label
        batch_number
        use_before_date use_before_date_formatted
      }
    }
  }
`;

export const removeProductBatchFromStock = gql`
  mutation RemoveProductBatchFromStock($stockCuid:String!, $productBatchCuid:String!) {
    removeProductBatchFromStock(stockCuid: $stockCuid, productBatchCuid: $productBatchCuid)
  }
`;

export const findProductBatches = gql`
  query FindProductBatches($searchString: String!, $adminUnitCuid: String, $includeDeleted: Boolean) {
    findProductBatches(searchString: $searchString, adminUnitCuid: $adminUnitCuid, includeDeleted: $includeDeleted) {
      cuid label
      created_at, deleted_at
      batch_number use_before_date use_before_date_formatted
      stocks {
        cuid label
        depot {
          cuid label
          administration_unit {cuid label}
        }
        depot_yard {cuid label}
      }
    }
  }
`;

@Injectable()
export class StockService extends VisbosService {

  @select() depot$: Observable<IDepot>;


  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions, private http: HttpClient) {
    super(apollo, ngRedux, sessionActions);
  }

  public getStocksForDepot(depotId: string | Observable<string>, adminUnitCuid?: string
    | Observable<string>): ApolloQueryObservable<Stock[]> {

    // Get all stocks from Server
    let data$ = this.fetchItems({
      query: fetchStocksForDepotQuery,
      variables: {
        adminUnitCuid: adminUnitCuid ? adminUnitCuid : this.activeAdminUnitCuid$,
        depotId: depotId
      }
    }).pipe(map(({data}: any) => data.fetchStocks));

    return <ApolloQueryObservable<Stock[]>>data$;
  }

  public getMedicineStock(cuid: string | Observable<string>): ApolloQueryObservable<Stock> {

    let data$ = this.fetchItems<Stock>({
      query: gql`
        query FetchMedicineStockDetailsQuery($stockId: String!) {
          fetchStockDetails(cuid: $stockId) {
            cuid, label, name,
            stock_item {
              cuid, label, name, short_name,
              medicine { ...MedicineFields },
            },
            depot { cuid, label },
            depot_yard { cuid, label, name },
            min_stock, report_stock, shall_stock, act_stock,
            order_amount, extraction_amount,
            registration_required,
            identities { cuid, label, ean, ean_type },
            product_batches { ...ProductBatchFields },
            in_shopping_cart, has_open_orders
          }
        }
        ${medicineFragments.medicine}
        ${productBatchFragments.productBatch}
      `,
      variables: {
        stockId: cuid
      }
    });

    return data$;

  }

  public getStock(cuid: string | Observable<string>): ApolloQueryObservable<Stock> {

    let data$ = this.fetchItems({
      query: gql`
        query FetchStockDetailsQuery($stockId: String!) {
          fetchStockDetails(cuid: $stockId) {
            ...StockDetailFields
          }
        }

        ${stockFragments['stock']}
      `,
      variables: {
        stockId: cuid
      }
    }).pipe(map(({data}: any) => data.fetchStockDetails));

    return <ApolloQueryObservable<Stock>>data$;

  }

  public getStockWithVariants(stockId: string, depotId: string): Observable<any> {

    let data$ = this.fetchItems({
      query: gql`
        query FetchStockVariantsQuery($stockId: String!, $depotId: String) {
          fetchStockVariants(stockId: $stockId, depotId: $depotId) {
            cuid, label,
            stock_item {cuid, label,
              properties {cuid, name, short_name, value}
            }
          }
        }`,
      variables: {
        stockId: stockId,
        depotId: depotId
      },
    });

    let model$ = data$.pipe(map(({data}: any) => {
        return {
          variants: data.fetchStockVariants,
          isLoading: data.loading
        };
      }
    ));

    return model$;
  }

  public getStocksForSubAdminUnits(adminUnitCuid?: string | Observable<string>, onlyCritical?: boolean) {
    return this.fetchItems({
      query: fetchStocksForSubAdminUnits,
      variables: {
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$,
        onlyCritical: onlyCritical
      }
    });
  }

  public getRelatedStocks(sourceStockCuid: string | Observable<string>): ApolloQueryObservable<FetchRelatedStocksQuery> {
    return this.fetchItems({
      query: fetchRelatedStocks,
      variables: {
        cuid: sourceStockCuid
      }
    });
  }

  //TODO: Refactor Apollo 2.0
  public getStocksInDepots(sourceDepotCuid, targetDepotCuid, onlyCritical?): ApolloQueryObservable<FetchStocksInDepotsQuery> {
    return this.fetchItems({
      query: fetchStocksInDepots,
      variables: {
        src: sourceDepotCuid,
        target: targetDepotCuid,
        critical: onlyCritical
      }
    })
  }

  public createStock(stockInput: StockInput): Observable<ApolloQueryResult<Stock>> {
    return this.mutate({
      mutation: gql`
        mutation CreateStockMutation($SI: CreateStockInput!) {
          createStock(stock_input: $SI) {
            ...StockDetailFields
          }
        }

        ${stockFragments.stock}
      `,
      variables: {
        SI: stockInput
      },
      refetchQueries: ['FetchStocksForDepotQuery', 'FetchStockVariantsQuery', 'FetchCriticalStocksQuery', 'LoadShoppingCart', 'LoadShoppingCartsForAdminUnit', 'FetchStockItemsQuery']
    }).pipe(
      catchError(err => {
        if (err.graphQLErrors && err.graphQLErrors.find(e => e.message.includes('Integrity constraint violation: 1062 Duplicate entry'))) {
          this.sessionActions.pushMessage('Bestand kann nicht angelegt werden, da bereits ein Bestand für den Artikel im Lager angelegt ist!', MessageType.WARNING, 'Bestandsverwaltung');
          return empty();
        }
        throw err;
      }),
      map((res: any) => res.data.createStock)
    );
  }

  public createStocksForDepot(depot_id: string, stockInput: StockInput[]): Observable<ApolloQueryResult<Stock>> {

    return this.mutate({
      mutation: gql`
        mutation AddStocksToDepotMutation($depot_id: String!, $stocks: [CreateStockInput]!) {
          createStocksForDepot(depot_id: $depot_id, stocks: $stocks) {
            ...StockDetailFields
          }
        }

        ${stockFragments.stock}
      `,
      variables: {
        depot_id: depot_id,
        stocks: stockInput
      },
      refetchQueries: ['FetchStocksForDepotQuery', 'FetchStockVariantsQuery', 'FetchCriticalStocksQuery', 'FetchStockItemsQuery', 'DepotsWithCriticalStocks', 'LoadShoppingCart', 'LoadShoppingCartsForAdminUnit']
    }).pipe(
      map((res: any) => res.data.createStock)
    );

  }

  public updateStocks(stocks: StockInput[], sync?: boolean): Observable<any> {

    if (!stocks || stocks.length == 0) {
      return of({});
    }

    let obs = [];
    for (let st of stocks) {
      obs.push(this.updateStock(st, st.cuid, sync));
    }

    return forkJoin(obs);
  }

  public updateStock(data: StockInput, cuid?: string, sync?: boolean): Observable<ApolloQueryResult<Stock>> {

    return this.mutate({
      mutation: gql`
        mutation UpdateStockMutation($cuid: String, $stock: CreateStockInput!, $sync: Boolean) {
          updateStock(cuid: $cuid, stock: $stock, sync: $sync){
            ...StockDetailFields
          }
        }

        ${stockFragments['stock']}
      `,
      variables: {
        cuid: cuid,
        stock: data,
        sync: sync
      },
      refetchQueries: ['FetchStocksForDepotQuery', 'FetchStockVariantsQuery', 'FetchCriticalStocksQuery', 'DepotsWithCriticalStocks', 'LoadShoppingCart', 'LoadShoppingCartsForAdminUnit']
    });

  }

  public deleteStock(cuid: string): Observable<ApolloQueryResult<any>> {

    let data$ = this.mutate<any>({
      mutation: gql`
        mutation DeleteStockMutation($cuid: String!) {
          deleteStock(cuid: $cuid)
        }`,
      variables: {
        cuid: cuid
      },
      refetchQueries: ['FetchStocksForDepotQuery', 'FetchStockVariantsQuery', 'FetchCriticalStocksQuery', 'FetchStockItemsQuery', 'DepotsWithCriticalStocks', 'LoadShoppingCart', 'LoadShoppingCartsForAdminUnit'],
      updateQueries: this.deleteItemsFromQueries(cuid, 'fetchStocks', 'FetchStocksForDepotQuery', 'FetchCriticalStocksQuery')
      //TODO: Remove from all queries!!
    });

    return data$;
  }

  public searchStocks(searchTerm: string, depotId?: string): Observable<any> {
    let data$ = this.fetchItems({
      query: gql`
        query FindStocks($searchString: String!, $depotId: String){
          findStocks(searchString: $searchString, depotId: $depotId){
            ...StockDetailFields
          }
        }
        ${stockFragments.stock}
      `,
      variables: {
        searchString: searchTerm,
        depotId: depotId
      }
    });

    let model$ = data$.pipe(map(({data}: any) => {
        return {
          stocks: data.findStocks,
          isLoading: data.loading,
          loading: data.loading
        };
      }
    ));

    return model$;
  }

  public searchStocksWithRelated(searchTerm: string, targetDepotId?: string, sourceDepotId?: string): Observable<any> {
    let data$ = this.fetchItems({
      query: gql`
        query FindStocksWithRelated($searchString: String!, $depotId: String, $sourceDepotId: String){
          findStocks(searchString: $searchString, depotId: $depotId, withAdditionalDepots: true){
            ...StockDetailFields
            required_amount
            related_stocks(depotCuid: $sourceDepotId) {
              ...StockDetailFields
            }
          }
        }
        ${stockFragments.stock}
      `,
      variables: {
        searchString: searchTerm,
        depotId: targetDepotId,
        sourceDepotId: sourceDepotId
      }
    });

    let model$ = data$.pipe(map(({data}: any) => {
        return {
          stocks: data.findStocks,
          isLoading: data.loading,
          loading: data.loading
        };
      }
    ));

    return model$;
  }

  public moveStocks(data: MoveStocksInput[], authData?: MedicineStockChangeInput, generateVouchers?: boolean, rearrangementData?: RearrangementInput): Observable<ApolloQueryResult<MoveStocksMutation>> {
    return this.mutate({
      mutation: moveStocks,
      variables: {
        data: data,
        auth_data: authData,
        generateVouchers: generateVouchers,
        rearrangementData: rearrangementData
      }
    });
  }

  //TODO: Apollo-Migration
  public fetchExpiringStocks(adminUnitCuid?, depotCuid?, categoryCuid?, supplierCuid?, expiringRangeDays?): ApolloQueryObservable<FetchExpiringStocksQuery> {
    return this.fetchItems<FetchExpiringStocksQuery>({
      query: fetchExpiringStocks,
      variables: {
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$,
        depotCuid, categoryCuid, supplierCuid, expiringRangeDays
      }
    })
  }

  public removeProductBatchFromStock(stockCuid: string, productBatchCuid: string): Observable<ApolloQueryResult<RemoveProductBatchFromStockMutation>> {
    return this.mutate({
      mutation: removeProductBatchFromStock,
      variables: {
        stockCuid, productBatchCuid
      },
      refetchQueries: ['FetchExpiringStocks']
    })
  }

  //TODO: Apollo Migration
  public findProductBatches(searchString, adminUnitCuid?, includeDeleted?, depotCuid?): ApolloQueryObservable<FindProductBatchesQuery> {
    return this.fetchItems({
      query: findProductBatches,
      variables: {
        searchString: searchString,
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$,
        includeDeleted: includeDeleted
      }
    });
  }

  generateStocklistExport(adminUnitCuid) {
    const headers = new HttpHeaders();
    headers.set('Accept', 'text/plain');
    return this.http.get(exportStockListEndpoint.replace(':adminUnitCuid', adminUnitCuid), {
      headers,
      responseType: 'arraybuffer',
      observe: 'response'
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/services/stock.service.ts